import { getAppStatus, RECONNECTED_EVENT, setAppStatus } from './appStatus.js';

/**
 * Notices a deploy while a tab is open. Every build stamps its id into the bundle (__APP_BUILD_ID__, vite.config.js)
 * and writes the same id to /version.json. The tab compares the two when the user is back on it; when they differ it says "A new
 * version of GeoPlay is available" (AppStatusBar) and updates itself on the user's next page change, when nothing on
 * screen can be lost. A dev server has no build id and never checks.
 */

export const CHECK_GAP_MS = 5 * 60 * 1000;
// One automatic reload per new build, so a load balancer still serving the old index.html cannot loop the tab.
const RELOADED_FOR_KEY = 'gp-reloaded-for-build';

/* global __APP_BUILD_ID__ */
export const RUNNING_BUILD_ID = typeof __APP_BUILD_ID__ === 'undefined' ? null : __APP_BUILD_ID__;

export const fetchDeployedBuildId = async () => {
    try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const body = await res.json();
        return typeof body?.buildId === 'string' ? body.buildId : null;
    } catch {
        return null;
    }
};

/** Ask the server which build it serves; flag a new version when it is not this one. */
export const checkForNewVersion = async (runningBuildId = RUNNING_BUILD_ID) => {
    if (!runningBuildId) return false;
    const deployed = await fetchDeployedBuildId();
    if (!deployed || deployed === runningBuildId) return false;
    setAppStatus({ newVersion: deployed });
    return true;
};

/** True the first time for a build (and remembers it), false after: reload for each new build at most once. */
export const claimReloadFor = (buildId) => {
    try {
        if (sessionStorage.getItem(RELOADED_FOR_KEY) === buildId) return false;
        sessionStorage.setItem(RELOADED_FOR_KEY, buildId);
    } catch {
        // No storage: still reload once - the reloaded page runs the new build and stops asking.
    }
    return true;
};

/**
 * Start watching (main.jsx). `router` is the data router: when a new version is waiting, the next completed page
 * change loads that page fresh instead of rendering it with the old code.
 */
export const startVersionWatch = (router, runningBuildId = RUNNING_BUILD_ID) => {
    if (!runningBuildId || typeof window === 'undefined') return () => {};

    // No timer: a tab nobody uses asks nothing. It asks when the user is back - on the tab, or on another page - at
    // most once per CHECK_GAP_MS, and at once when the connection returns (a deploy is the usual cause of the gap).
    let lastCheck = Date.now();
    const check = () => {
        lastCheck = Date.now();
        checkForNewVersion(runningBuildId);
    };
    const checkIfDue = () => { if (Date.now() - lastCheck >= CHECK_GAP_MS) check(); };
    const onVisible = () => { if (document.visibilityState === 'visible') checkIfDue(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(RECONNECTED_EVENT, check);

    let lastPath = router?.state?.location?.pathname;
    const unsubscribe = router?.subscribe?.((state) => {
        const { location, navigation } = state;
        if (navigation && navigation.state !== 'idle') return;
        if (location.pathname === lastPath) return;
        lastPath = location.pathname;
        const waiting = getAppStatus().newVersion;
        if (waiting && claimReloadFor(waiting)) {
            window.location.assign(`${location.pathname}${location.search}${location.hash}`);
            return;
        }
        checkIfDue();
    }) || (() => {});

    return () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener(RECONNECTED_EVENT, check);
        unsubscribe();
    };
};
